import { ColumnDef } from '@tanstack/react-table'
import { OverflownText } from 'components/OverflownText'
import { useMemo, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { PiMagnifyingGlass } from 'react-icons/pi'

import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import { DataTableContainer } from 'components/DataTable/UI'
import { IconContainer, Input } from 'components/Filters/TableSearch'
import * as CommonS from 'components/Tables/CommonTable.styles'
import Checkbox from 'components/Checkbox/checkbox'
import {
  type EffectivePreferenceSetting,
  useGetEffectiveNotificationPreferencesByKind,
  useUpdateAllNotificationPreferencesByKind,
  useUpdateNotificationPreference,
} from 'services/notifications'

import * as S from './styles'

export function NotificationPreferencesTable({ tab }: { tab: string }) {
  const { data: rawData, isLoading } =
    useGetEffectiveNotificationPreferencesByKind({ kind_slug: tab })
  const [searchQuery, setSearchQuery] = useState('')

  // Client-side search: all data is loaded at once (no pagination), so filtering in-memory is sufficient.
  const data = useMemo(() => {
    if (!searchQuery || !rawData) return rawData
    const q = searchQuery.toLowerCase()
    return rawData.filter((item) =>
      item.preference_description?.toLowerCase().includes(q)
    )
  }, [rawData, searchQuery])
  const { mutate: mutateUpdateAllNotificationsPreferencesByKind } =
    useUpdateAllNotificationPreferencesByKind()
  const { mutate: mutateUpdateNotificationsPreferences } =
    useUpdateNotificationPreference()

  const handleCheckAll = async (data: {
    emailEnabled?: boolean
    inAppEnabled?: boolean
  }) => {
    mutateUpdateAllNotificationsPreferencesByKind({
      kind_slug: tab,
      data: {
        email_enabled: data.emailEnabled,
        in_app_enabled: data.inAppEnabled,
      },
    })
  }

  const handleCheckOne = async (
    preferenceSlug: string,
    data: {
      emailEnabled?: boolean
      inAppEnabled?: boolean
    }
  ) => {
    mutateUpdateNotificationsPreferences({
      kind_slug: tab,
      data: {
        preference: preferenceSlug,
        email_enabled: data.emailEnabled,
        in_app_enabled: data.inAppEnabled,
      },
    })
  }

  const columns = useMemo<ColumnDef<EffectivePreferenceSetting, any>[]>(() => {
    const currentColunms: ColumnDef<EffectivePreferenceSetting, any>[] = [
      {
        header: 'Notification',
        cell: ({ row }) => {
          return row.original?.preference_description
        },
      },
      {
        id: 'all-email',
        size: 40,
        header: () => {
          const allChecked = rawData
            ?.filter((row) => row.is_enabled_email)
            .every((row) => row.email_enabled)
          return (
            <div className="flex justify-center gap-2">
              <Checkbox
                checked={allChecked}
                onChange={(e) =>
                  handleCheckAll({
                    emailEnabled: e.target.checked,
                  })
                }
              />
              <span>Email</span>
            </div>
          )
        },
        cell: ({ row }) => {
          if (row.original.is_enabled_email) {
            return (
              <Checkbox
                checked={row.original.email_enabled}
                onChange={(e) =>
                  handleCheckOne(row.original.preference_slug, {
                    emailEnabled: e.target.checked,
                  })
                }
              />
            )
          }

          return null
        },
      },
      {
        id: 'all-in-app',
        size: 40,
        meta: {
          isLastColumn: true,
        },
        header: () => {
          const allChecked = rawData
            ?.filter((row) => row.is_enabled_in_app)
            .every((row) => row.in_app_enabled)
          return (
            <div className="flex justify-center gap-2">
              <Checkbox
                checked={allChecked}
                onChange={(e) =>
                  handleCheckAll({ inAppEnabled: e.target.checked })
                }
              />
              <span>In App</span>
            </div>
          )
        },
        cell: ({ row }) => {
          if (row.original.is_enabled_in_app) {
            return (
              <Checkbox
                checked={row.original.in_app_enabled}
                onChange={(e) =>
                  handleCheckOne(row.original.preference_slug, {
                    inAppEnabled: e.target.checked,
                  })
                }
              />
            )
          }

          return null
        },
      },
    ]

    return currentColunms
  }, [rawData])

  if (isLoading || !rawData) {
    return (
      <div className="flex justify-center items-center h-[500px] w-full">
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <DataTableContainer>
      <ClientDataTable
        height={600}
        tableHeader={
          <>
            <S.SettingsTableHeader>
              <S.TableDescriptionText>
                <OverflownText
                  tooltipText={`Select how you prefer to be notified about ${tab}.`}
                  maxLines={4}
                  placement="bottom"
                >
                  Select how you prefer to be notified about{' '}
                  <strong>{tab}</strong>.
                </OverflownText>
              </S.TableDescriptionText>
            </S.SettingsTableHeader>
            <CommonS.SearchContainer>
              <CommonS.SearchContainerFilters>
                <CommonS.TableSearchContainer>
                  <div style={{ position: 'relative' }}>
                    <IconContainer>
                      <PiMagnifyingGlass />
                    </IconContainer>
                    <Input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search notifications..."
                      className="form-control"
                      style={{ width: '100%' }}
                    />
                  </div>
                </CommonS.TableSearchContainer>
              </CommonS.SearchContainerFilters>
            </CommonS.SearchContainer>
          </>
        }
        data={data || []}
        columns={columns}
      />
    </DataTableContainer>
  )
}
